import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../api/client';
import Modal from '../components/Modal';
import { Plus, Users, Building2, Briefcase, Trash2, Power, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { isValidPhone } from '../lib/validate';
import PasswordInput from '../components/PasswordInput';

export default function Admin() {
  const [tab, setTab] = useState('users');
  return (
    <div className="space-y-6" data-testid="admin-page">
      <header>
        <div className="overline">Module 08 · Admin</div>
        <h1 className="heading text-3xl font-black text-ink">Admin Panel</h1>
        <p className="text-sm text-zinc-500 mt-1">Manage users, departments, job titles.</p>
      </header>

      <div className="flex gap-1 border-b border-zinc-200">
        <TabBtn active={tab === 'users'} onClick={() => setTab('users')} icon={Users} label="Users" dt="tab-users" />
        <TabBtn active={tab === 'departments'} onClick={() => setTab('departments')} icon={Building2} label="Departments" dt="tab-departments" />
        <TabBtn active={tab === 'jobs'} onClick={() => setTab('jobs')} icon={Briefcase} label="Job Titles" dt="tab-jobs" />
      </div>

      {tab === 'users' && <UsersTab />}
      {tab === 'departments' && <DepartmentsTab />}
      {tab === 'jobs' && <JobTitlesTab />}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, dt }) {
  return (
    <button onClick={onClick} data-testid={dt}
      className={`px-4 py-2 text-sm font-bold flex items-center gap-2 border-b-2 ${active ? 'border-primary text-primary' : 'border-transparent text-zinc-500'}`}
    ><Icon size={14} /> {label}</button>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get('/users');
    setUsers(data);
    setSupervisors(data.filter((u) => u.role === 'supervisor'));
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    await api.patch(`/users/${u.id}`, { active: !u.active });
    toast.success(`${u.name} ${!u.active ? 'activated' : 'deactivated'}`);
    load();
  };

  // Find supervisor name for display
  const supName = (u) => {
    if (!u.supervisor_id) return null;
    const s = users.find((x) => x.id === u.supervisor_id);
    return s?.name ?? '—';
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button className="btn-primary" onClick={() => setOpenCreate(true)} data-testid="add-user-btn"><Plus size={14} /> Add User</button>
      </div>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-zinc-50 border-b border-zinc-200 text-left">
            {['Name', 'Email', 'Role', 'Department', 'Employee Code', 'Supervisor', 'Status', ''].map((h) => <th key={h} className="px-3 py-2 overline">{h}</th>)}
          </tr></thead>
          <tbody data-testid="users-table">
            {users.map((u) => (
              <tr key={u.id} className="border-b border-zinc-100">
                <td className="px-3 py-2 font-semibold">{u.name}</td>
                <td className="px-3 py-2 text-xs font-mono">{u.email}</td>
                <td className="px-3 py-2"><span className="badge-muted">{u.role}</span></td>
                <td className="px-3 py-2">{u.department || '—'}</td>
                <td className="px-3 py-2 text-xs font-mono">{u.employee_code || '—'}</td>
                <td className="px-3 py-2 text-xs text-zinc-500">
                  {u.role === 'admin' || u.role === 'supervisor' ? (
                    <span className="text-zinc-400 italic">n/a</span>
                  ) : u.supervisor_id ? (
                    <span className="text-green-700 font-medium">{supName(u)}</span>
                  ) : (
                    <span className="text-red-600 font-semibold">Unassigned</span>
                  )}
                </td>
                <td className="px-3 py-2"><span className={u.active ? 'badge-success' : 'badge-danger'}>{u.active ? 'Active' : 'Inactive'}</span></td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <button className="btn-outline text-xs" onClick={() => setEditing(u)} data-testid={`edit-user-${u.id}`}>
                      <Pencil size={12} /> Edit
                    </button>
                    <button className="btn-outline text-xs" onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`}>
                      <Power size={12} /> {u.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {openCreate && <CreateUserModal onClose={() => setOpenCreate(false)} onCreated={() => { setOpenCreate(false); load(); }} supervisors={supervisors} />}
      {editing && <EditUserModal user={editing} supervisors={supervisors} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CreateUserModal({ onClose, onCreated, supervisors }) {
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'employee', department: '', employee_code: '', phone: '', supervisor_id: '' });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [depts, setDepts] = useState([]);
  useEffect(() => { api.get('/departments').then(({ data }) => setDepts(data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.phone && !isValidPhone(form.phone)) {
      setErr('Phone: Enter a valid 10-digit Indian mobile number starting with 6, 7, 8, or 9');
      return;
    }
    setErr(''); setLoading(true);
    try {
      // Supervisor is saved with the user in the same request.
      await api.post('/auth/register', { ...form, supervisor_id: needsSupervisor ? form.supervisor_id || undefined : undefined });
      toast.success('User created');
      onCreated();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail, e?.response?.data));
    } finally { setLoading(false); }
  };

  const needsSupervisor = !['admin', 'supervisor'].includes(form.role);

  return (
    <Modal open onClose={onClose} title="Add User" size="md"
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button form="u-form" type="submit" className="btn-primary" disabled={loading} data-testid="submit-user">Create</button>
      </>}
    >
      <form id="u-form" onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><label className="label">Name *</label><input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="u-name" /></div>
          <div><label className="label">Email *</label><input required type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="u-email" /></div>
          <div><label className="label">Password *</label><PasswordInput required className="input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" data-testid="u-password" /></div>
          <div><label className="label">Role *</label>
            <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, supervisor_id: '' })}>
              {['admin', 'hr', 'supervisor', 'guard', 'employee'].map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div><label className="label">Department</label>
            <select className="input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
              <option value="">—</option>
              {depts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>
          </div>
          <div><label className="label">Employee Code</label><input className="input" value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} /></div>
          <div className="col-span-2"><label className="label">Phone</label><input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          {needsSupervisor && (
            <div className="col-span-2">
              <label className="label">Supervisor {supervisors.length === 0 ? '(no supervisors exist yet)' : ''}</label>
              <select className="input" value={form.supervisor_id} onChange={(e) => setForm({ ...form, supervisor_id: e.target.value })}>
                <option value="">— Unassigned</option>
                {supervisors.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.department || 'no dept'})</option>)}
              </select>
              {!form.supervisor_id && <p className="text-xs text-amber-600 mt-1">Without a supervisor, this user cannot submit gate pass requests.</p>}
            </div>
          )}
        </div>
        {err && <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-2 rounded-sm">{err}</div>}
      </form>
    </Modal>
  );
}

function EditUserModal({ user, supervisors, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || '',
    role: user.role || 'employee',
    department: user.department || '',
    employee_code: user.employee_code || '',
    phone: user.phone || '',
    supervisor_id: user.supervisor_id || '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [depts, setDepts] = useState([]);
  useEffect(() => { api.get('/departments').then(({ data }) => setDepts(data)); }, []);

  const needsSupervisor = !['admin', 'supervisor'].includes(form.role);

  const submit = async (e) => {
    e.preventDefault();
    if (form.phone && !isValidPhone(form.phone)) {
      setErr('Phone: Enter a valid 10-digit Indian mobile number starting with 6, 7, 8, or 9');
      return;
    }
    if (form.password) {
      if (form.password.length < 4) { setErr('New password must be at least 4 characters.'); return; }
      if (form.password !== form.confirmPassword) { setErr('Passwords do not match.'); return; }
    }
    setErr(''); setLoading(true);
    try {
      const payload = {
        name: form.name,
        role: form.role,
        department: form.department,
        employee_code: form.employee_code,
        phone: form.phone,
        supervisor_id: needsSupervisor ? (form.supervisor_id || null) : null,
        ...(form.password ? { password: form.password } : {}),
      };
      await api.patch(`/users/${user.id}`, payload);
      toast.success('User updated');
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.message || formatApiError(e?.response?.data?.detail, e?.response?.data) || 'Update failed');
    } finally { setLoading(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Edit — ${user.name}`} size="md"
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button form="edit-form" type="submit" className="btn-primary" disabled={loading}>Save Changes</button>
      </>}
    >
      <form id="edit-form" onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><label className="label">Name *</label><input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><label className="label">Role</label>
            <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, supervisor_id: '' })}>
              {['admin', 'hr', 'supervisor', 'guard', 'employee'].map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div><label className="label">Department</label>
            <select className="input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
              <option value="">—</option>
              {depts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>
          </div>
          <div><label className="label">Employee Code</label><input className="input" value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} /></div>
          <div className="col-span-2"><label className="label">Phone</label><input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          {needsSupervisor && (
            <div className="col-span-2">
              <label className="label">Supervisor</label>
              <select className="input" value={form.supervisor_id} onChange={(e) => setForm({ ...form, supervisor_id: e.target.value })}>
                <option value="">— Unassigned</option>
                {supervisors.filter((s) => s.id !== user.id).map((s) => <option key={s.id} value={s.id}>{s.name} ({s.department || 'no dept'})</option>)}
              </select>
              {!form.supervisor_id && <p className="text-xs text-amber-600 mt-1">Without a supervisor, this user cannot submit gate pass requests.</p>}
            </div>
          )}
        </div>
        <div className="border-t border-zinc-100 pt-4 mt-2">
          <p className="overline mb-3">Reset Password <span className="normal-case text-zinc-400 font-normal">(leave blank to keep current)</span></p>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">New Password</label><PasswordInput className="input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" /></div>
            <div><label className="label">Confirm Password</label><PasswordInput className="input" value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} autoComplete="new-password" /></div>
          </div>
        </div>
        {err && <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-2 rounded-sm">{err}</div>}
      </form>
    </Modal>
  );
}

function DepartmentsTab() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState('');
  const load = async () => { const { data } = await api.get('/departments'); setItems(data); };
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try { await api.post('/departments', { name }); setName(''); load(); toast.success('Added'); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail, e?.response?.data)); }
  };
  const [deleting, setDeleting] = useState(null);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="card p-4">
        <div className="overline mb-3">Add Department</div>
        <form onSubmit={add} className="flex gap-2">
          <input className="input" placeholder="Department name" value={name} onChange={(e) => setName(e.target.value)} data-testid="dept-input" />
          <button className="btn-primary" data-testid="add-dept"><Plus size={14} /> Add</button>
        </form>
      </div>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-zinc-50 border-b border-zinc-200 text-left"><th className="px-3 py-2 overline">Department</th><th></th></tr></thead>
          <tbody data-testid="dept-table">
            {items.map((d) => (
              <tr key={d.id} className="border-b border-zinc-100">
                <td className="px-3 py-2 font-semibold">{d.name}</td>
                <td className="px-3 py-2 text-right"><button className="btn-ghost p-1.5" onClick={() => setDeleting(d)} data-testid={`del-dept-${d.id}`}><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {deleting && (
        <DeleteDepartmentModal
          dept={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); load(); }}
        />
      )}
    </div>
  );
}

// A department still holding active users can't be deleted (the backend enforces this too):
// the admin is shown who's in it and must move them via Users → Edit first.
// Deactivated users don't count.
function DeleteDepartmentModal({ dept, onClose, onDeleted }) {
  const [members, setMembers] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/users')
      .then(({ data }) => setMembers(data.filter((u) => u.active && u.department === dept.name)))
      .catch((e) => setLoadErr(formatApiError(e?.response?.data?.detail, e?.response?.data)));
  }, [dept.name]);

  const blocked = members && members.length > 0;

  const confirm = async () => {
    setErr(''); setLoading(true);
    try {
      await api.delete(`/departments/${dept.id}`);
      toast.success('Department deleted');
      onDeleted();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail, e?.response?.data));
    } finally { setLoading(false); }
  };

  return (
    <Modal open onClose={onClose} title="Delete Department" subtitle={dept.name} size="md"
      footer={<>
        <button className="btn-outline" onClick={onClose}>{blocked ? 'Close' : 'Cancel'}</button>
        {!blocked && (
          <button className="btn-danger" onClick={confirm} disabled={loading || !members} data-testid="confirm-del-dept">Delete</button>
        )}
      </>}
    >
      <div className="space-y-3 text-sm">
        {!members && !loadErr && <p className="text-zinc-500">Checking users in this department…</p>}
        {loadErr && <div className="text-red-700 bg-red-50 border border-red-200 p-2 rounded-sm">Couldn't check users: {loadErr}</div>}
        {members && !blocked && <p>Delete <span className="font-bold">{dept.name}</span>? This can't be undone.</p>}
        {blocked && (
          <>
            <div className="text-amber-800 bg-amber-50 border border-amber-200 p-3 rounded-sm" data-testid="dept-has-users">
              <span className="font-bold">{members.length} active user{members.length === 1 ? ' is' : 's are'} still in {dept.name}.</span>{' '}
              Move {members.length === 1 ? 'them' : 'each of them'} to another department (Users tab → Edit) before deleting it.
            </div>
            <ul className="border border-zinc-200 rounded-sm divide-y divide-zinc-100 max-h-60 overflow-auto">
              {members.map((u) => (
                <li key={u.id} className="px-3 py-2 flex justify-between gap-2">
                  <span className="font-semibold text-ink">{u.name}</span>
                  <span className="text-xs text-zinc-500 font-mono">{u.employee_code || u.email} · {u.role}</span>
                </li>
              ))}
            </ul>
          </>
        )}
        {err && <div className="text-red-700 bg-red-50 border border-red-200 p-2 rounded-sm">{err}</div>}
      </div>
    </Modal>
  );
}

function JobTitlesTab() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState('');
  const load = async () => { const { data } = await api.get('/job-titles'); setItems(data); };
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try { await api.post('/job-titles', { title }); setTitle(''); load(); toast.success('Added'); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail, e?.response?.data)); }
  };
  const del = async (id) => { await api.delete(`/job-titles/${id}`); load(); };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="card p-4">
        <div className="overline mb-3">Add Job Title</div>
        <form onSubmit={add} className="flex gap-2">
          <input className="input" placeholder="e.g. Operator" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="job-input" />
          <button className="btn-primary" data-testid="add-job"><Plus size={14} /> Add</button>
        </form>
      </div>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-zinc-50 border-b border-zinc-200 text-left"><th className="px-3 py-2 overline">Title</th><th></th></tr></thead>
          <tbody data-testid="job-table">
            {items.map((d) => (
              <tr key={d.id} className="border-b border-zinc-100">
                <td className="px-3 py-2 font-semibold">{d.title}</td>
                <td className="px-3 py-2 text-right"><button className="btn-ghost p-1.5" onClick={() => del(d.id)} data-testid={`del-job-${d.id}`}><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
